import { parsePivot, pivotOffsetPx, rotateAround } from './pivot';
function rotatePoint(x, y, cx, cy, deg) {
    return rotateAround(x, y, cx, cy, deg);
}
function pivotInBox(boxX, boxY, width, height, pivotPoint) {
    const off = pivotOffsetPx(parsePivot(pivotPoint), width, height);
    return { x: boxX + off.x, y: boxY + off.y };
}
/** Parent chain from root → … → immediate parent (excludes `objectId`). */
export function getAncestorChain(objectId, objectsById) {
    const chain = [];
    const seen = new Set();
    let current = objectsById.get(objectId);
    while (current?.parentObjectId) {
        if (seen.has(current.parentObjectId))
            break;
        seen.add(current.parentObjectId);
        const parent = objectsById.get(current.parentObjectId);
        if (!parent)
            break;
        chain.unshift(parent);
        current = parent;
    }
    return chain;
}
/**
 * World-space top-left + cumulative rotation.
 * Matches CSS: absolute left/top inside parent, then rotate about each box's pivot.
 */
export function getWorldPose(obj, objectsById) {
    const chain = [...getAncestorChain(obj.id, objectsById), obj];
    let worldX = 0;
    let worldY = 0;
    let worldW = 0;
    let worldH = 0;
    let worldRot = 0;
    let parentPivotPoint;
    let hasParent = false;
    for (const node of chain) {
        if (!hasParent) {
            worldX = node.x;
            worldY = node.y;
            worldW = node.width;
            worldH = node.height;
            worldRot = node.rotation;
            parentPivotPoint = node.pivotPoint;
            hasParent = true;
            continue;
        }
        const pivot = pivotInBox(worldX, worldY, worldW, worldH, parentPivotPoint);
        const tl = rotatePoint(worldX + node.x, worldY + node.y, pivot.x, pivot.y, worldRot);
        worldX = tl.x;
        worldY = tl.y;
        worldW = node.width;
        worldH = node.height;
        worldRot += node.rotation;
        parentPivotPoint = node.pivotPoint;
    }
    return { x: worldX, y: worldY, rotation: worldRot };
}
/** World bounds for marquee / hit-testing. */
export function getWorldBounds(obj, objectsById) {
    const pose = getWorldPose(obj, objectsById);
    const pivot = parsePivot(obj.pivotPoint);
    return {
        x: pose.x,
        y: pose.y,
        width: obj.width,
        height: obj.height,
        rotation: pose.rotation,
        originX: pivot.ox,
        originY: pivot.oy,
    };
}
/**
 * Convert a world-space pose (top-left + rotation) into parent-local space.
 * If `parent` is null, world === local.
 */
export function worldPoseToLocal(world, parent, objectsById) {
    if (!parent)
        return { ...world };
    const parentWorld = getWorldPose(parent, objectsById);
    const pivot = pivotInBox(parentWorld.x, parentWorld.y, parent.width, parent.height, parent.pivotPoint);
    const unrotated = rotatePoint(world.x, world.y, pivot.x, pivot.y, -parentWorld.rotation);
    return {
        x: unrotated.x - parentWorld.x,
        y: unrotated.y - parentWorld.y,
        rotation: world.rotation - parentWorld.rotation,
    };
}
/** Convert parent-local pose to world-space. */
export function localPoseToWorld(local, parent, objectsById) {
    if (!parent)
        return { ...local };
    const parentWorld = getWorldPose(parent, objectsById);
    const pivot = pivotInBox(parentWorld.x, parentWorld.y, parent.width, parent.height, parent.pivotPoint);
    const worldTl = rotatePoint(parentWorld.x + local.x, parentWorld.y + local.y, pivot.x, pivot.y, parentWorld.rotation);
    return {
        x: worldTl.x,
        y: worldTl.y,
        rotation: local.rotation + parentWorld.rotation,
    };
}
export function buildObjectsById(objects) {
    const map = new Map();
    for (const obj of objects)
        map.set(obj.id, obj);
    return map;
}
export function buildChildrenByParent(objects) {
    const map = new Map();
    for (const obj of objects) {
        if (!obj.parentObjectId)
            continue;
        const list = map.get(obj.parentObjectId);
        if (list)
            list.push(obj);
        else
            map.set(obj.parentObjectId, [obj]);
    }
    for (const list of map.values()) {
        list.sort((a, b) => a.zIndex - b.zIndex || a.name.localeCompare(b.name));
    }
    return map;
}
export function getRootObjects(objects) {
    return objects
        .filter((o) => !o.parentObjectId)
        .sort((a, b) => a.zIndex - b.zIndex);
}
export function collectDescendantIds(rootId, childrenByParent) {
    const ids = new Set();
    const stack = [...(childrenByParent.get(rootId) ?? [])].map((o) => o.id);
    while (stack.length > 0) {
        const id = stack.pop();
        if (ids.has(id))
            continue;
        ids.add(id);
        const kids = childrenByParent.get(id);
        if (kids) {
            for (const kid of kids)
                stack.push(kid.id);
        }
    }
    return ids;
}
/**
 * Layout patch when moving `obj` under `newParent` (or to root if null).
 * Preserves world visual position/rotation.
 */
export function reparentLayoutPatch(obj, newParent, objects) {
    const objectsById = buildObjectsById(objects);
    const world = getWorldPose(obj, objectsById);
    const local = worldPoseToLocal(world, newParent, objectsById);
    return {
        parentObjectId: newParent ? newParent.id : null,
        x: Math.round(local.x),
        y: Math.round(local.y),
        rotation: local.rotation,
    };
}
/** Axis-aligned bbox of objects in world space (accounts for rotation extents). */
export function getWorldAxisAlignedBounds(objs, objectsById) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const obj of objs) {
        const pose = getWorldPose(obj, objectsById);
        const pivot = parsePivot(obj.pivotPoint);
        const ox = pose.x + pivot.ox * obj.width;
        const oy = pose.y + pivot.oy * obj.height;
        const corners = [
            { x: pose.x, y: pose.y },
            { x: pose.x + obj.width, y: pose.y },
            { x: pose.x + obj.width, y: pose.y + obj.height },
            { x: pose.x, y: pose.y + obj.height },
        ];
        for (const c of corners) {
            const p = rotateAround(c.x, c.y, ox, oy, pose.rotation);
            minX = Math.min(minX, p.x);
            minY = Math.min(minY, p.y);
            maxX = Math.max(maxX, p.x);
            maxY = Math.max(maxY, p.y);
        }
    }
    if (!Number.isFinite(minX)) {
        return { x: 0, y: 0, width: 100, height: 100 };
    }
    return {
        x: Math.round(minX),
        y: Math.round(minY),
        width: Math.max(1, Math.round(maxX - minX)),
        height: Math.max(1, Math.round(maxY - minY)),
    };
}
export function isGroupContainer(obj) {
    return obj.type === 'group' || obj.isGroup === true;
}
/** Immediate parent if it is a group container; otherwise null. */
export function getGroupParent(obj, objectsById) {
    if (!obj.parentObjectId)
        return null;
    const parent = objectsById.get(obj.parentObjectId);
    if (!parent || !isGroupContainer(parent))
        return null;
    return parent;
}
